/**
 * ErrorBoundary component for graceful error handling.
 * Catches React errors and displays a user-friendly fallback UI.
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4">
          <div className="max-w-md w-full">
            <div className="card shadow-elegant text-center">
              <div className="mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h1 className="text-3xl font-serif font-bold text-primary-800 mb-3">
                  Oops! Something went wrong
                </h1>
                <p className="text-neutral-600 mb-2">
                  We encountered an unexpected error. Don't worry, your data is safe.
                </p>
                {this.state.error && (
                  <details className="mt-4 text-left">
                    <summary className="text-sm text-neutral-500 cursor-pointer hover:text-neutral-700">
                      Technical details
                    </summary>
                    <div className="mt-2 p-3 bg-red-50 rounded-lg text-xs font-mono text-red-800 overflow-auto">
                      {this.state.error.message}
                    </div>
                  </details>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="btn btn-primary px-6 py-3"
                >
                  Return to Home
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-outline px-6 py-3"
                >
                  Reload Page
                </button>
              </div>

              <p className="mt-6 text-xs text-neutral-500">
                If this problem persists, please contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
